import useTranslations from "@/shared/lang/useTranslations";
import { cn } from "@/shared/lib/utils";

import { SELL_HISTORY_FILTERS, type CryptoSellFilterId } from "../../constants/cryptoSwaps.constants";
import { sellPillVariant } from "../../utils/cryptoSellStatus";

/** Mirrors `ColocaStatusPill`'s variants so a chip and the rows it filters read as the same colour. */
const DOT_CLASS: Record<string, string> = {
  info: "bg-blue-700 dark:bg-blue-300",
  success: "bg-success",
  warning: "bg-warning",
  danger: "bg-destructive",
  neutral: "bg-muted-foreground",
};

export interface CryptoSellStatusChipsProps {
  value: CryptoSellFilterId;
  onChange: (id: CryptoSellFilterId) => void;
}

export function CryptoSellStatusChips({ value, onChange }: CryptoSellStatusChipsProps) {
  const { t } = useTranslations("cryptoSwaps");

  return (
    <div role="group" aria-label={t("history_filter_group")} className="flex flex-wrap gap-2">
      {SELL_HISTORY_FILTERS.map((filter) => {
        const selected = value === filter.id;
        const dot = filter.status ? DOT_CLASS[sellPillVariant(filter.status)] : null;

        return (
          <button
            key={filter.id}
            type="button"
            aria-pressed={selected}
            onClick={() => onChange(filter.id)}
            className={cn(
              "inline-flex h-10 items-center gap-2 rounded-full border px-4 text-[13px] transition-colors",
              "focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-ring",
              selected
                ? "border-foreground bg-foreground font-semibold text-background"
                : "border-border bg-card font-medium text-foreground hover:border-ring/40",
            )}
          >
            {dot && <span aria-hidden="true" className={cn("size-1.5 rounded-full", dot)} />}
            {t(filter.labelKey)}
          </button>
        );
      })}
    </div>
  );
}
